import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { useRideDialog } from './RideDialogProvider';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Chip,
  Button,
  TextField,
  InputAdornment,
  Grid,
  Typography,
  Menu,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Badge,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Card,
  CardContent,
  Divider,
  Stack,
  useMediaQuery
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  Edit as EditIcon,
  MoreVert as MoreVertIcon,
  CheckCircle as CompletedIcon,
  LocalTaxi as TaxiIcon,
  DirectionsCar as CarIcon,
  Person as DriverIcon,
  Schedule as ScheduledIcon,
  Cancel as CancelledIcon,
  AccessTime as RequestedIcon,
  DirectionsRun as InProgressIcon,
  Delete as DeleteIcon,
  Event as EventIcon,
  FilterList as FilterListIcon,
  FlashOn as FlashOnIcon,
  Warning as WarningIcon,
  Repeat as RecurringIcon,
  ContentCopy as ContentCopyIcon,
  Phone as PhoneIcon,
  Smartphone as SmartphoneIcon,
  Timer as TimerIcon,
  PlayArrow as StartIcon,
  Stop as EndIcon,
  SwapHoriz as SwapHorizIcon,
  AirportShuttle as KombiIcon,
  ElectricRickshaw as MinibusIcon,
  LocalHospital as MedicalIcon,
  Accessible as WheelchairIcon,
  EventSeat as TransportChairIcon,
  Hotel as StretcherIcon,
  StickyNote2 as NoteIcon
} from '@mui/icons-material';
import { styled, useTheme } from '@mui/material/styles';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { de } from 'date-fns/locale';

import {
  fetchRides,
  // OLD LISTENERS DEAKTIVIERT - AppDataManager übernimmt Datenladung
  // startRidesStatusListener,
  // stopRidesStatusListener,
  deleteRide,
  updateRideStatus
} from '../../redux/slices/ridesSlice';
import { isCentralOperationsRole, isCentralRole } from '../../redux/slices/authSlice';
import { fetchMedicalTransportOrders } from '../../redux/slices/medicalTransportOrdersSlice';
import { formatVehicleWithCapabilities, migrateSpecialVehicleType } from '../../config/vehicleTypes';
import { getActualRideDurationMinutes } from '../../utils/rideDuration';
import { getCancellationShortLabel } from '../../utils/cancellationInfo';
import PaymentChip from '../common/PaymentChip';
import { isAppPaidRide } from '../../utils/payment';
import {
  ACTIVE_RIDE_STATUSES as ACTIVE_STATUSES,
  getRideReferenceMs,
  getRideSortKey,
  getRideTier,
  getTimeRangeBounds,
  isAppBooking,
  isImminentPreAccepted,
  isUnassignedAppPreOrder,
  isVenueBooking,
} from '../../utils/rideDashboardFilters';
import { RIDE_STATUS_FILTER_LABELS, getRideStatusLabel } from '../../utils/rideStatusLabels';
import PageHeader from '../common/PageHeader';
import Loading from '../common/Loading';
import ErrorMessage from '../common/ErrorMessage';
import NoDestinationRideSnackbar from './NoDestinationRideSnackbar';

const StyledTableRow = styled(TableRow)(({ theme, isOverdue }) => ({
  '&:nth-of-type(odd)': {
    backgroundColor: isOverdue ? 'rgba(255, 68, 68, 0.1)' : theme.palette.action.hover,
  },
  '&:hover': {
    backgroundColor: isOverdue ? 'rgba(255, 68, 68, 0.2)' : theme.palette.action.selected,
  },
  ...(isOverdue && {
    backgroundColor: 'rgba(255, 68, 68, 0.08)',
    borderLeft: `4px solid #ff4444`,
  }),
  // hide last border
  '&:last-child td, &:last-child th': {
    border: 0,
  },
}));

// Helper-Funktionen für StatusChip
const getIconForStatus = (status) => {
  switch (status) {
    case 'completed':
      return <CompletedIcon />;
    case 'in_progress':
      return <InProgressIcon />;
    case 'arrived':
      return <DriverIcon />;
    case 'accepted':
      return <DriverIcon />;
    case 'pre_accepted':
      return <DriverIcon />;
    case 'open':
      return <RequestedIcon />;
    case 'dispatcher_pending':
      return <RequestedIcon />;
    case 'cancelled':
      return <CancelledIcon />;
    case 'cancelled_by_driver':
      return <CancelledIcon />;
    case 'scheduled':
      return <ScheduledIcon />;
    case 'paused':
      return <WarningIcon />;
    case 'dispatch_exhausted':
      return <WarningIcon />;
    default:
      return <RequestedIcon />;
  }
};

const getColorForStatus = (status, theme) => {
  switch (status) {
    case 'completed':
      return theme.palette.success.main;
    case 'in_progress':
      return theme.palette.primary.main;
    case 'arrived':
      // Teal – klar abgegrenzt von success (completed) und info (accepted),
      // semantisch passend als Wartezustand beim Kunden.
      return '#00897b';
    case 'accepted':
      return theme.palette.info.main;
    case 'open':
      return theme.palette.warning.main;
    case 'dispatch_exhausted':
      return theme.palette.error.main;
    case 'dispatcher_pending':
      return theme.palette.warning.main;
    case 'cancelled':
      return theme.palette.error.main;
    case 'cancelled_by_driver':
      return theme.palette.error.dark;
    case 'scheduled':
      return theme.palette.secondary.main;
    case 'pre_accepted':
      // Wie 'scheduled': die Fahrt liegt noch in der Zukunft. Vom hellblauen
      // info.light war sie neben 'accepted' (info.main) kaum zu unterscheiden;
      // gegen 'scheduled' grenzt sie das Fahrer-Icon ab.
      return theme.palette.secondary.main;
    case 'paused':
      return theme.palette.warning.dark;
    default:
      return undefined;
  }
};

// Vereinfachte StatusChip ohne verschachtelte Arrow-Funktionen
const StatusChip = styled(Chip)(({ theme, status }) => ({
  color: status === 'default' ? 'inherit' : 'white',
  backgroundColor: getColorForStatus(status, theme),
  '& .MuiSvgIcon-root': {
    fontSize: '1rem',
  }
}));

// Füge einen neuen styled Badge hinzu
const StyledBadge = styled(Badge)(({ theme }) => ({
  '& .MuiBadge-badge': {
    backgroundColor: theme.palette.error.main,
    color: theme.palette.error.contrastText,
  },
}));

const isDispatchExhausted = (ride) => ride?.dispatchState === 'exhausted';

const getDisplayStatus = (ride) => {
  if (ride?.status === 'open' && isDispatchExhausted(ride)) {
    return 'dispatch_exhausted';
  }
  return ride?.status;
};

const getStatusDetailLabel = (ride) => {
  if (ride?.status === 'cancelled' && isDispatchExhausted(ride)) {
    return 'Nicht vermittelbar';
  }
  return getCancellationShortLabel(ride);
};

const DISPATCH_SOURCE_LABEL_MAP = {
  sector_queue: 'Sektor-Angebot',
  distance_fallback: 'Fallback-Angebot',
  distance_direct: 'Entfernungsangebot',
  predicted_sector_followup: 'Anschlusstour'
};

const OFFER_TYPE_LABEL_MAP = {
  immediate: 'Sofortangebot',
  auto_follow_up: 'Anschlussangebot'
};

// Labels für den Verordnungsstatus (Verordnungs-Spalte + kompaktes Icon/Tooltip).
const MEDICAL_PROOF_LABEL_MAP = {
  missing: 'Nachweis fehlt',
  incomplete: 'Unvollständig',
  complete: 'Nachweis vollständig',
  waived: 'Nicht möglich',
  problem: 'Problem',
  rejected: 'Beanstandet'
};

const MEDICAL_PROOF_TYPE_LABEL_MAP = {
  digital_signature: 'Signatur',
  paper_proof: 'Papier',
  facility_confirmation: 'Einrichtung',
  cannot_sign: 'Keine Unterschrift',
  no_show: 'No-Show',
  problem: 'Problem'
};

const MEDICAL_BILLING_LABEL_MAP = {
  not_ready: 'Nicht bereit',
  ready: 'Bereit',
  exported: 'Exportiert',
  submitted: 'Eingereicht',
  paid: 'Bezahlt',
  rejected: 'Beanstandet'
};

const FINAL_RIDE_STATUSES = ['completed', 'cancelled', 'cancelled_by_driver'];
const DUPLICATABLE_RIDE_STATUSES = [
  ...FINAL_RIDE_STATUSES,
  'open',
  'dispatcher_pending',
  'accepted',
  'pre_accepted',
  'arrived',
  'in_progress',
  'scheduled'
];
const ASSIGNABLE_RIDE_STATUSES = ['scheduled', 'open', 'dispatcher_pending', 'pre_accepted', 'accepted'];
const CANCELLABLE_RIDE_STATUSES = ['open', 'scheduled', 'dispatcher_pending', 'accepted', 'arrived', 'pre_accepted', 'in_progress', 'paused'];

const getDriverDisplayName = (driver) =>
  driver?.name || driver?.user?.name || driver?.displayName || driver?.email || null;

const shortId = (value) => {
  if (!value || typeof value !== 'string') return null;
  return value.length > 10 ? `${value.substring(0, 10)}...` : value;
};

const isAutoAcceptedOpenPreOrder = (ride) =>
  ride.orderType === 1 &&
  ride.autoAccepted === true &&
  ride.status === 'scheduled' &&
  !ride.driver &&
  !ride.driverId &&
  !ride.isPhoneCustomer;

const DATE_FORMATTER = new Intl.DateTimeFormat('de-DE', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
});

const TIME_FORMATTER = new Intl.DateTimeFormat('de-DE', {
  hour: '2-digit',
  minute: '2-digit',
});

// Wartezeit-Severity analog zum Dashboard (grün/orange/rot je nach Wartezeit)
const getWaitTimeSeverity = (waitTime) => {
  const value = String(waitTime || '');
  if (value.includes('nicht verfügbar') ||
      value.includes('Keine Fahrer verfügbar') ||
      value.includes('30+') ||
      value.includes('Fehler')) return 'error';
  if (value.includes('nach Ausfallzeit') ||
      value.startsWith('2') ||
      value.startsWith('3')) return 'warning';
  return 'success';
};

const RidesList = () => {
  const dispatch = useDispatch();
  const { openRideDialog } = useRideDialog() || {};
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  // Verwende neues AppData System statt altes rides slice (memoized)
  const rides = useSelector(state => state.appData.rides);
  const drivers = useSelector(state => state.appData.drivers);
  const vehicles = useSelector(state => state.appData.vehicles);
  const isLoading = useSelector(state => state.appData.isLoading.rides);
  const error = useSelector(state => state.appData.error.rides);
  const currentUser = useSelector(state => state.auth.user);
  // Verordnungen für den Live-documentStatus der Verordnungs-Spalte (der im
  // Ride gespeicherte medicalTransportSnapshot wird nach Anlage nicht mehr
  // aktualisiert und würde „unvollständig" dauerhaft anzeigen).
  const medicalTransportOrders = useSelector(state => state.medicalTransportOrders.orders);
  const canDeleteRides = isCentralRole(currentUser);
  const canEditRide = useCallback((ride) => (
    isCentralOperationsRole(currentUser) ||
    (currentUser?.operatorId && ride?.createdByOperatorId === currentUser.operatorId)
  ), [currentUser]);
  
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [orderTypeFilter, setOrderTypeFilter] = useState('all');
  const [rideTypeFilter, setRideTypeFilter] = useState('all');
  const [timeRange, setTimeRange] = useState('today_plus_12h');
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const [customStart, setCustomStart] = useState(null);
  const [customEnd, setCustomEnd] = useState(null);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [menuRide, setMenuRide] = useState(null);
  
  const [dataLoaded, setDataLoaded] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [rideToDelete, setRideToDelete] = useState(null);

  // Echtzeit-Wartezeiten aus Firebase
  const [waitTimes, setWaitTimes] = useState(null);
  const waitTimeUnsubRef = useRef(null);

  useEffect(() => {
    const unsub = onSnapshot(doc(db, 'waitTimes', 'current'), (snap) => {
      if (snap.exists()) {
        const d = snap.data();
        setWaitTimes({
          general: d.general || '–',
          grossraum: d.grossraum || '–',
          kleinbus7: d.kleinbus7 || '–',
          kleinbus8: d.kleinbus8 || '–',
        });
      }
    });
    waitTimeUnsubRef.current = unsub;
    return () => unsub();
  }, []);

  // Lookup: driverId → Fahrzeugdaten (Fallback wenn driverModel im Ride-Dokument fehlt)
  const vehicleByDriverId = useMemo(() => {
    if (!vehicles || vehicles.length === 0) return {};
    const map = {};
    vehicles.forEach(v => {
      const dId = v.driverId || v.current_driver?.id;
      if (dId) {
        map[dId] = { model: v.model, licensePlate: v.licensePlate, vehicleNumber: v.vehicleNumber };
      }
    });
    return map;
  }, [vehicles]);

  const driverById = useMemo(() => {
    if (!drivers || drivers.length === 0) return {};
    return drivers.reduce((map, driver) => {
      if (driver.id) {
        map[driver.id] = driver;
      }
      return map;
    }, {});
  }, [drivers]);

  // Ermittle welche Fahrzeugtypen tatsächlich existieren
  const existingVehicleTypes = useMemo(() => {
    if (!vehicles || vehicles.length === 0) return new Set([1]); // PKW als Minimum
    const types = new Set();
    vehicles.forEach(v => {
      const vType = Number(v.vehicleType || v.vehicle_type || 1);
      types.add(vType);
    });
    return types;
  }, [vehicles]);

  // Verfügbarkeit von Krankenfahrt-Spezialfahrzeugen (Rollstuhl/Tragestuhl/Liegend):
  // Pro Capability geprüft, ob mindestens ein registriertes Fahrzeug aktuell
  // einem online angemeldeten Fahrer zugewiesen ist. Pause/aktive Fahrt zählt
  // weiterhin als verfügbar, da der Fahrer im Dienst ist.
  const specialCapabilityItems = useMemo(() => {
    const driverById = (drivers || []).reduce((map, d) => {
      if (d?.id) map[d.id] = d;
      return map;
    }, {});

    const checkCapability = (capability) => {
      let hasAny = false;
      let isAvailable = false;
      for (const vehicle of (vehicles || [])) {
        const caps = Array.isArray(vehicle.specialCapabilities) ? vehicle.specialCapabilities : [];
        if (!caps.includes(capability)) continue;
        hasAny = true;
        const driverId = vehicle.driverId || vehicle.current_driver?.id || vehicle.current_driver_id || null;
        const driver = driverId ? driverById[driverId] : null;
        if (driver && driver.isOnline) {
          isAvailable = true;
          break;
        }
      }
      return { hasAny, isAvailable };
    };

    const definitions = [
      { key: 'rollstuhl', label: 'Rollstuhl', icon: <WheelchairIcon /> },
      { key: 'tragestuhl', label: 'Tragestuhl', icon: <TransportChairIcon /> },
      { key: 'liegend', label: 'Liegend', icon: <StretcherIcon /> },
    ];

    return definitions
      .map((def) => ({ ...def, ...checkCapability(def.key) }))
      .filter((item) => item.hasAny);
  }, [drivers, vehicles]);

  useEffect(() => {
    const loadRides = async () => {
      try {
        // Alle Fahrten laden (inkl. abgeschlossene und stornierte)
        await dispatch(fetchRides({
          status: ['open', 'accepted', 'in_progress', 'arrived', 'scheduled', 'pre_accepted', 'completed', 'cancelled', 'cancelled_by_driver', 'paused']
        }));
        setDataLoaded(true);
      } catch (error) {
        console.error("RidesList: Fehler beim Laden der Fahrtdaten:", error);
        setDataLoaded(false);
      }
    };

    if (!dataLoaded) {
      loadRides();
    }
  }, [dispatch, dataLoaded]);

  useEffect(() => {
    dispatch(fetchMedicalTransportOrders());
  }, [dispatch]);

  const medicalOrderById = useMemo(() => {
    const map = {};
    (medicalTransportOrders || []).forEach((order) => {
      if (order?.id) map[order.id] = order;
    });
    return map;
  }, [medicalTransportOrders]);

  // „Verordnung unvollständig": verlinkte Verordnung existiert, aber ihr
  // documentStatus ist (noch) nicht 'complete' — der erwartete Normalzustand
  // direkt nach einer Schnellanlage, bis die Zentrale nachpflegt. Bewusst vom
  // roten „Ohne Verordnung" (gar keine Verlinkung) getrennt. Live-Daten haben
  // Vorrang; der Ride-Snapshot dient nur als Fallback (z. B. archivierte
  // Verordnung, die nicht mehr im Store liegt).
  const isMedicalOrderIncomplete = useCallback((ride) => {
    if (!ride.medicalTransportOrderId) return false;
    const liveOrder = medicalOrderById[ride.medicalTransportOrderId];
    if (liveOrder) return (liveOrder.documentStatus || 'incomplete') !== 'complete';
    const snapshotStatus = ride.medicalTransportSnapshot?.documentStatus;
    return !!snapshotStatus && snapshotStatus !== 'complete';
  }, [medicalOrderById]);

  const handleChangePage = useCallback((event, newPage) => {
    setPage(newPage);
  }, []);

  const handleChangeRowsPerPage = useCallback((event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  }, []);

  const handleSearch = useCallback((event) => {
    setSearchTerm(event.target.value);
    setPage(0);
  }, []);

  const handleStatusFilterChange = useCallback((event) => {
    setStatusFilter(event.target.value);
    setPage(0);
  }, []);

  const handleOrderTypeFilterChange = useCallback((event) => {
    setOrderTypeFilter(event.target.value);
    setPage(0);
  }, []);

  const handleRideTypeFilterChange = useCallback((event) => {
    setRideTypeFilter(event.target.value);
    setPage(0);
  }, []);

  const handleTimeRangeChange = useCallback((event) => {
    setTimeRange(event.target.value);
    setPage(0);
  }, []);

  const handleCustomStartChange = useCallback((value) => {
    setCustomStart(value);
    setPage(0);
  }, []);

  const handleCustomEndChange = useCallback((value) => {
    setCustomEnd(value);
    setPage(0);
  }, []);

  const handleCloseMenu = useCallback(() => {
    setMenuAnchor(null);
    setMenuRide(null);
  }, []);

  const handleOpenMenu = useCallback((event, ride) => {
    setMenuAnchor(event.currentTarget);
    setMenuRide(ride);
  }, []);

  const handleDeleteClick = useCallback((ride) => {
    setRideToDelete(ride);
    setDeleteDialogOpen(true);
    setMenuAnchor(null);
    setMenuRide(null);
  }, []);

  const handleDeleteConfirm = useCallback(async () => {
    try {
      await dispatch(deleteRide(rideToDelete.id)).unwrap();
      setDeleteDialogOpen(false);
      setRideToDelete(null);
    } catch (error) {
      console.error('Fehler beim Löschen der Fahrt:', error);
    }
  }, [dispatch, rideToDelete]);

  const handleCancelRide = useCallback(async (ride) => {
    if (!ride) return;
    if (['in_progress', 'paused'].includes(ride.status) &&
        !window.confirm('Diese Fahrt wurde bereits angetreten (Fahrgast an Bord). Wirklich stornieren?')) {
      handleCloseMenu();
      return;
    }
    try {
      await dispatch(updateRideStatus({
        rideId: ride.id,
        status: 'cancelled',
        additionalData: {
          cancelledBy: currentUser?.uid || null,
          cancelledByRole: currentUser?.role || null
        }
      })).unwrap();
    } catch (error) {
      console.error('Fehler beim Stornieren der Fahrt:', error);
    } finally {
      handleCloseMenu();
    }
  }, [currentUser, dispatch, handleCloseMenu]);

  const handleRideStatusAction = useCallback(async (ride, status) => {
    if (!ride) return;
    try {
      await dispatch(updateRideStatus({
        rideId: ride.id,
        status
      })).unwrap();
    } catch (error) {
      console.error('Fehler beim Aktualisieren des Fahrtstatus:', error);
    } finally {
      handleCloseMenu();
    }
  }, [dispatch, handleCloseMenu]);

  const handleReleaseRide = useCallback(async (ride) => {
    if (!ride) return;
    try {
      await dispatch(updateRideStatus({
        rideId: ride.id,
        status: 'scheduled',
        additionalData: {
          driverId: null,
          driverName: null,
          driverProfilePic: null,
          driverRating: null,
          driverRatingCount: null,
          driverModel: null,
          driverLicensePlate: null,
          driverVehicleId: null,
          vehicleOperatorId: null,
          acceptedAt: null,
          acceptedTimestamp: null,
          assignedByDispatcher: false,
          currentOfferDriverId: null,
          currentOfferType: null,
          currentOfferStartedAt: null,
          currentOfferExpiresAt: null,
          dispatchAction: null
        }
      })).unwrap();
    } catch (error) {
      console.error('Fehler beim Zurücknehmen der Fahrt:', error);
    } finally {
      handleCloseMenu();
    }
  }, [dispatch, handleCloseMenu]);

  const handleDeleteCancel = useCallback(() => {
    setDeleteDialogOpen(false);
    setRideToDelete(null);
  }, []);
  
  // Fahrtbezogen, damit eine Vorbestellung im Status 'accepted' als
  // "Angetreten" erscheint statt als "Akzeptiert".
  const translateStatus = useCallback((ride, status) => getRideStatusLabel(ride, status), []);

  const formatDate = useCallback((dateInput) => {
    if (!dateInput) return '-';
    try {
      let date;
      if (dateInput.toDate) {
        date = dateInput.toDate();
      } else if (dateInput.seconds) {
        date = new Date(dateInput.seconds * 1000);
      } else {
        date = new Date(dateInput);
      }
      if (isNaN(date.getTime())) return '-';
      return DATE_FORMATTER.format(date);
    } catch (error) {
      console.error('Fehler beim Formatieren des Datums:', error, dateInput);
      return '-';
    }
  }, []);

  const formatTime = useCallback((dateInput) => {
    if (!dateInput) return '-';
    try {
      let date;
      if (dateInput.toDate) {
        date = dateInput.toDate();
      } else if (dateInput.seconds) {
        date = new Date(dateInput.seconds * 1000);
      } else {
        date = new Date(dateInput);
      }
      if (isNaN(date.getTime())) return '-';
      return TIME_FORMATTER.format(date);
    } catch (error) {
      console.error('Fehler beim Formatieren der Uhrzeit:', error, dateInput);
      return '-';
    }
  }, []);

  // Kompakter Verordnungsstatus als Icon in der „Fahrt"-Zelle. Farbe = Nachweis-
  // status, Tooltip (titleAccess) = Patient · Nachweis · Abrechnung.
  const renderMedicalStatusIcon = useCallback((ride) => {
    if (!ride.medicalTransportOrderId) {
      if (ride.isKrankenfahrt) {
        return (
          <MedicalIcon
            titleAccess="Krankenfahrt ohne Verordnung"
            sx={{ fontSize: '1.1rem', color: 'warning.main' }}
          />
        );
      }
      return null;
    }
    const snapshot = ride.medicalTransportSnapshot || {};
    const proofStatus = ride.medicalTransportProofStatus || 'missing';
    const billingStatus = ride.medicalTransportBillingStatus || 'not_ready';
    const proofColor = proofStatus === 'complete'
      ? 'success.main'
      : proofStatus === 'problem' || proofStatus === 'rejected'
        ? 'error.main'
        : proofStatus === 'waived'
          ? 'info.main'
          : 'warning.main';
    const title = [
      snapshot.patientName || 'Verordnung',
      ...(isMedicalOrderIncomplete(ride) ? ['Verordnung unvollständig'] : []),
      `Nachweis: ${MEDICAL_PROOF_LABEL_MAP[proofStatus] || proofStatus}`,
      `Abrechnung: ${MEDICAL_BILLING_LABEL_MAP[billingStatus] || billingStatus}`,
    ].join(' · ');
    return (
      <MedicalIcon titleAccess={title} sx={{ fontSize: '1.1rem', color: proofColor }} />
    );
  }, [isMedicalOrderIncomplete]);

  // Volle Verordnungs-Zelle (eigene Spalte): Patient, Kasse, Nachweis-, Abrechnungs-
  // und Nachweistyp-Status als Chips.
  const renderMedicalTransportInfo = useCallback((ride) => {
    if (!ride.medicalTransportOrderId) {
      if (ride.isKrankenfahrt) {
        return (
          <Chip
            size="small"
            label="Ohne Verordnung"
            color="warning"
            variant="outlined"
          />
        );
      }
      return (
        <Typography variant="body2" color="textSecondary">
          -
        </Typography>
      );
    }

    const snapshot = ride.medicalTransportSnapshot || {};
    const proofStatus = ride.medicalTransportProofStatus || 'missing';
    const billingStatus = ride.medicalTransportBillingStatus || 'not_ready';
    const proofType = ride.medicalTransportProofType || '';
    const hasSignature = Array.isArray(ride.medicalTransportSignaturePoints) && ride.medicalTransportSignaturePoints.length > 0;
    const proofColor = proofStatus === 'complete'
      ? 'success'
      : proofStatus === 'problem' || proofStatus === 'rejected'
        ? 'error'
        : proofStatus === 'waived'
          ? 'info'
          : 'warning';
    const billingColor = billingStatus === 'ready' || billingStatus === 'paid'
      ? 'success'
      : billingStatus === 'rejected'
        ? 'error'
        : billingStatus === 'submitted' || billingStatus === 'exported'
          ? 'info'
          : 'default';

    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, alignItems: 'center' }}>
        <Chip
          icon={<MedicalIcon />}
          size="small"
          label={snapshot.patientName || 'Verordnung'}
          color="error"
          variant="outlined"
          sx={{ maxWidth: 180 }}
        />
        {snapshot.healthInsuranceName && (
          <Typography variant="caption" color="textSecondary" noWrap sx={{ maxWidth: 180 }}>
            {snapshot.healthInsuranceName}
          </Typography>
        )}
        {isMedicalOrderIncomplete(ride) && (
          <Chip
            size="small"
            label="Verordnung unvollständig"
            color="warning"
            variant="outlined"
            sx={{ height: 20, fontSize: '0.7rem' }}
          />
        )}
        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', justifyContent: 'center' }}>
          <Chip
            size="small"
            label={MEDICAL_PROOF_LABEL_MAP[proofStatus] || proofStatus}
            color={proofColor}
            variant="outlined"
            sx={{ height: 20, fontSize: '0.7rem' }}
          />
          <Chip
            size="small"
            label={MEDICAL_BILLING_LABEL_MAP[billingStatus] || billingStatus}
            color={billingColor}
            variant="outlined"
            sx={{ height: 20, fontSize: '0.7rem' }}
          />
          {proofType && (
            <Chip
              size="small"
              label={MEDICAL_PROOF_TYPE_LABEL_MAP[proofType] || proofType}
              color={proofType === 'no_show' || proofType === 'problem' ? 'error' : 'info'}
              variant="outlined"
              sx={{ height: 20, fontSize: '0.7rem' }}
            />
          )}
          {hasSignature && (
            <Chip
              size="small"
              label="Signatur vorhanden"
              color="success"
              variant="outlined"
              sx={{ height: 20, fontSize: '0.7rem' }}
            />
          )}
        </Box>
      </Box>
    );
  }, [isMedicalOrderIncomplete]);

  // Eine Zeile der „Zeiten"-Spalte: Label linksbündig, Zeitstempel rechtsbündig,
  // Farbe passend zum jeweiligen Status-Chip (getColorForStatus).
  const renderTimeRow = useCallback((label, value, status, colorOverride) => {
    const color = colorOverride || getColorForStatus(status, theme) || theme.palette.text.primary;
    return (
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 1, minWidth: 0 }}>
        <Typography component="span" variant="body2" noWrap sx={{ fontWeight: 600, color, flexShrink: 0 }}>
          {label}
        </Typography>
        <Typography component="span" variant="body2" noWrap sx={{ color, textAlign: 'right', minWidth: 0 }}>
          {value}
        </Typography>
      </Box>
    );
  }, [theme]);

  // Fahrpreis-Zelle: tatsächlich gezahlter (App-Capture), tatsächlich erfasster,
  // fester oder geschätzter Fahrpreis — mit Quellen-Kennzeichnung.
  const renderFareCell = useCallback((ride) => {
    let amount = null;
    let source = '';
    const capturedCents = Number(ride.paymentCapturedAmountCents);
    if (isAppPaidRide(ride) && capturedCents > 0) {
      amount = capturedCents / 100;
      source = 'Bezahlt';
    } else if (ride.isFestpreis && Number(ride.festpreisEur) > 0) {
      amount = Number(ride.festpreisEur);
      source = 'Festpreis';
    } else if (Number(ride.fare) > 0) {
      amount = Number(ride.fare);
      source = 'Tatsächlich';
    } else if (Number(ride.estimatedEarningEur) > 0) {
      amount = Number(ride.estimatedEarningEur);
      source = 'Geschätzt';
    }

    if (amount === null) {
      return (
        <Typography variant="body2" color="textSecondary">
          -
        </Typography>
      );
    }

    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {amount.toFixed(2).replace('.', ',')} €
        </Typography>
        <Typography
          variant="caption"
          color={source === 'Geschätzt' ? 'text.secondary' : 'text.primary'}
        >
          {source}
        </Typography>
      </Box>
    );
  }, []);

  const isRideOverdue = useCallback((ride) => {
    if (ride.status !== 'pre_accepted') return false;
    if (!ride.scheduledTime) return false;
    try {
      let scheduledDate;
      if (ride.scheduledTime.toDate) {
        scheduledDate = ride.scheduledTime.toDate();
      } else if (ride.scheduledTime.seconds) {
        scheduledDate = new Date(ride.scheduledTime.seconds * 1000);
      } else {
        scheduledDate = new Date(ride.scheduledTime);
      }
      const minutesUntilStart = Math.ceil((scheduledDate.getTime() - Date.now()) / (1000 * 60));
      return minutesUntilStart <= 5;
    } catch (error) {
      console.error('Fehler beim Prüfen der Überfälligkeit:', error);
      return false;
    }
  }, []);

  const getDelayInfo = useCallback((ride) => {
    if (ride.orderType !== 1 || !ride.scheduledTime) return null;
    try {
      let scheduledDate;
      if (ride.scheduledTime.toDate) {
        scheduledDate = ride.scheduledTime.toDate();
      } else if (ride.scheduledTime.seconds) {
        scheduledDate = new Date(ride.scheduledTime.seconds * 1000);
      } else {
        scheduledDate = new Date(ride.scheduledTime);
      }

      let actualStartDate = null;
      if (ride.startedAt) {
        if (ride.startedAt.toDate) {
          actualStartDate = ride.startedAt.toDate();
        } else if (ride.startedAt.seconds) {
          actualStartDate = new Date(ride.startedAt.seconds * 1000);
        } else {
          actualStartDate = new Date(ride.startedAt);
        }
      } else if (ride.passengerPickedUpTimestamp) {
        if (ride.passengerPickedUpTimestamp.toDate) {
          actualStartDate = ride.passengerPickedUpTimestamp.toDate();
        } else if (ride.passengerPickedUpTimestamp.seconds) {
          actualStartDate = new Date(ride.passengerPickedUpTimestamp.seconds * 1000);
        } else {
          actualStartDate = new Date(ride.passengerPickedUpTimestamp);
        }
      } else if (ride.status === 'pre_accepted') {
        const now = new Date();
        if (now > scheduledDate) {
          const delayMinutes = Math.floor((now.getTime() - scheduledDate.getTime()) / (1000 * 60));
          return { isDelayed: true, delayMinutes, status: 'pending' };
        }
        return null;
      }

      if (actualStartDate) {
        const delayMs = actualStartDate.getTime() - scheduledDate.getTime();
        const delayMinutes = Math.floor(delayMs / (1000 * 60));
        return {
          isDelayed: delayMinutes > 0,
          delayMinutes: Math.abs(delayMinutes),
          status: delayMinutes > 0 ? 'delayed' : (delayMinutes < 0 ? 'early' : 'ontime'),
          actualStartDate,
        };
      }
      return null;
    } catch (error) {
      console.error('Fehler beim Berechnen der Verspätung:', error);
      return null;
    }
  }, []);

  // Filter und Sort – wird nur neu berechnet wenn rides oder Filterkriterien sich ändern.
  // Echtzeit-Updates von Firebase (via AppDataManager → Redux) triggern automatisch eine
  // Neuberechnung, da `rides` als Dependency enthalten ist.
  const filteredData = useMemo(() => {
    const term = searchTerm.toLowerCase();
    const bounds = getTimeRangeBounds(timeRange, customStart, customEnd);
    const nowMs = Date.now();
    const isHistoricRange = timeRange === 'yesterday';

    return rides.filter(ride => {
      const currentOfferDriver = ride.currentOfferDriverId
        ? driverById[ride.currentOfferDriverId]
        : null;
      const currentOfferDriverName = getDriverDisplayName(currentOfferDriver) || ride.currentOfferDriverName || '';

      const searchMatch = !searchTerm ||
        (ride.start || ride.pickup_address)?.toLowerCase().includes(term) ||
        (ride.destination || ride.destination_address)?.toLowerCase().includes(term) ||
        (ride.customerName || ride.customer?.name)?.toLowerCase().includes(term) ||
        (ride.medicalTransportSnapshot?.patientName || '')?.toLowerCase().includes(term) ||
        (ride.medicalTransportSnapshot?.healthInsuranceName || '')?.toLowerCase().includes(term) ||
        (ride.medicalTransportSnapshot?.approvalNumber || '')?.toLowerCase().includes(term) ||
        (ride.medicalTransportSnapshot?.prescriptionNumber || '')?.toLowerCase().includes(term) ||
        (ride.driverName || ride.driver?.user?.name)?.toLowerCase().includes(term) ||
        currentOfferDriverName.toLowerCase().includes(term) ||
        (ride.driverLicensePlate || ride.vehicle?.licensePlate)?.toLowerCase().includes(term) ||
        (ride.driverModel || ride.vehicle?.model)?.toLowerCase().includes(term);
      const statusMatch = statusFilter === 'all' || ride.status === statusFilter;
      const orderTypeMatch = orderTypeFilter === 'all' ||
        (orderTypeFilter === '1' && ride.orderType === 1) ||
        (orderTypeFilter === '2' && ride.orderType === 2);
      const rideTypeMatch = rideTypeFilter === 'all' ||
        (rideTypeFilter === 'krankenfahrt' && ride.isKrankenfahrt) ||
        (rideTypeFilter === 'rechnungsfahrt' && ride.isRechnungsfahrt) ||
        (rideTypeFilter === 'app' && isAppBooking(ride));

      // Zeitraum-Filter: aktive Fahrten, überfällige/bevorstehende Vorbestellungen
      // sowie nicht zugewiesene App-Vorbestellungen sind immer sichtbar –
      // unabhängig vom gewählten Zeitraum. Bei „Gestern" ist die Ansicht rein
      // historisch, dort würden diese Ausnahmen nur heutige Fahrten einmischen.
      let timeRangeMatch = true;
      if (
        bounds &&
        (isHistoricRange ||
          (!ACTIVE_STATUSES.has(ride.status) &&
            !isUnassignedAppPreOrder(ride) &&
            !isImminentPreAccepted(ride, nowMs)))
      ) {
        const refMs = getRideReferenceMs(ride);
        timeRangeMatch = refMs !== null && refMs >= bounds.start && refMs <= bounds.end;
      }

      return searchMatch &&
        statusMatch &&
        orderTypeMatch &&
        rideTypeMatch &&
        timeRangeMatch;
    }).sort((a, b) => {
      const tierA = getRideTier(a, nowMs);
      const tierB = getRideTier(b, nowMs);
      if (tierA !== tierB) return tierA - tierB;
      return getRideSortKey(a, tierA) - getRideSortKey(b, tierB);
    });
  }, [
    rides,
    driverById,
    searchTerm,
    statusFilter,
    orderTypeFilter,
    rideTypeFilter,
    timeRange,
    customStart,
    customEnd
  ]);

  // Pagination – neu berechnet nur wenn filteredData, page oder rowsPerPage sich ändern
  const paginatedData = useMemo(
    () => filteredData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [filteredData, page, rowsPerPage]
  );
  
  if (isLoading) {
    return <Loading message="Fahrten werden geladen..." />;
  }
  
  if (error) {
    return (
      <ErrorMessage
        title="Fehler beim Laden der Fahrten"
        message={error}
        onRetry={() => dispatch(fetchRides())}
      />
    );
  }
  
  return (
    <Box sx={{ flexGrow: 1, p: { xs: 1.5, sm: 2, md: 3 } }}>
      <PageHeader
        title="Fahrtverwaltung"
        description="Übersicht und Verwaltung aller Fahrten"
        breadcrumbs={[
          { label: 'Dashboard', path: '/dashboard' },
          { label: 'Fahrten', path: '/fahrten' }
        ]}
        actionButton={{
          label: 'Fahrt hinzufügen',
          icon: <AddIcon />,
          onClick: () => openRideDialog?.({ mode: 'create' })
        }}
        secondaryButtons={[
          {
            label: 'Daueraufträge',
            icon: <RecurringIcon />,
            path: '/fahrten/dauerauftraege'
          },
          {
            label: 'Krankenfahrten',
            icon: <MedicalIcon />,
            path: '/krankenfahrten'
          },
          {
            label: 'Abrechnung',
            icon: <MedicalIcon />,
            path: '/krankenfahrten/abrechnung'
          }
        ]}
      />

      {/* Wartezeit-Anzeige – Optik analog zum Dashboard */}
      {waitTimes && (() => {
        const waitTimeItems = [
          { key: 'general', label: 'PKW', value: waitTimes.general, icon: <CarIcon /> },
        ];
        if (existingVehicleTypes.has(2)) {
          waitTimeItems.push({ key: 'grossraum', label: 'Großraum', value: waitTimes.grossraum, icon: <KombiIcon /> });
        }
        if (existingVehicleTypes.has(3)) {
          waitTimeItems.push({ key: 'kleinbus7', label: 'Kleinbus 7', value: waitTimes.kleinbus7, icon: <MinibusIcon /> });
        }
        if (existingVehicleTypes.has(4)) {
          waitTimeItems.push({ key: 'kleinbus8', label: 'Kleinbus 8', value: waitTimes.kleinbus8, icon: <MinibusIcon /> });
        }

        return (
          <Paper
            elevation={0}
            sx={{
              px: 2,
              py: 1.5,
              mb: 2,
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'divider',
              background: 'linear-gradient(180deg, rgba(255,255,255,0.98), rgba(248,250,252,0.98))',
              boxShadow: '0 1px 3px rgba(15, 23, 42, 0.08)',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: { xs: 'flex-start', md: 'center' },
                justifyContent: 'space-between',
                gap: 1.5,
                flexWrap: 'wrap',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 190 }}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 34,
                    height: 34,
                    borderRadius: 1,
                    flexShrink: 0,
                    bgcolor: '#1976d218',
                    '& .MuiSvgIcon-root': { fontSize: 20, color: '#1976d2' },
                  }}
                >
                  <TimerIcon />
                </Box>
                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 800, lineHeight: 1.2 }}>
                    Aktuelle Wartezeiten
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Nach Fahrzeugtyp
                  </Typography>
                </Box>
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', flex: 1 }}>
                {waitTimeItems.map((item) => {
                  const severity = getWaitTimeSeverity(item.value);
                  const softBg = severity === 'error'
                    ? 'rgba(211, 47, 47, 0.08)'
                    : severity === 'warning'
                      ? 'rgba(237, 108, 2, 0.08)'
                      : 'rgba(46, 125, 50, 0.08)';

                  return (
                    <Chip
                      key={item.key}
                      icon={item.icon}
                      label={`${item.label} ${item.value || '–'}`}
                      size="small"
                      variant="outlined"
                      sx={{
                        height: 30,
                        borderColor: `${severity}.main`,
                        color: `${severity}.dark`,
                        bgcolor: softBg,
                        fontWeight: 700,
                        '& .MuiChip-icon': { color: `${severity}.dark` },
                      }}
                    />
                  );
                })}

                {specialCapabilityItems.length > 0 && (
                  <MedicalIcon
                    fontSize="small"
                    sx={{ color: 'error.main', ml: 0.5 }}
                    titleAccess="Krankenfahrt-Spezialfahrzeuge"
                  />
                )}
                {specialCapabilityItems.map((item) => {
                  const severity = item.isAvailable ? 'success' : 'error';
                  const softBg = item.isAvailable
                    ? 'rgba(46, 125, 50, 0.08)'
                    : 'rgba(211, 47, 47, 0.08)';
                  return (
                    <Chip
                      key={item.key}
                      icon={item.icon}
                      label={`${item.label} ${item.isAvailable ? 'Verfügbar' : 'Nicht verfügbar'}`}
                      size="small"
                      variant="outlined"
                      sx={{
                        height: 30,
                        borderColor: `${severity}.main`,
                        color: `${severity}.dark`,
                        bgcolor: softBg,
                        fontWeight: 700,
                        '& .MuiChip-icon': { color: `${severity}.dark` },
                      }}
                    />
                  );
                })}
              </Box>
            </Box>
          </Paper>
        );
      })()}

      {/* Filter und Suche */}
      <Paper sx={{ p: { xs: 1.5, sm: 2 }, mb: { xs: 2, md: 3 } }}>
        <Grid container spacing={2} alignItems="center">
          <Grid size={{ xs: 12, sm: 3 }}>
            <TextField
              fullWidth
              variant="outlined"
              label="Suche (Adresse, Kunde, Fahrer, Kennzeichen)"
              value={searchTerm}
              onChange={handleSearch}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid size={{ xs: 12 }} sx={{ display: { xs: 'block', sm: 'none' } }}>
            <Stack direction="row" spacing={1} sx={{ overflowX: 'auto', pb: 0.5 }}>
              <Chip
                clickable
                label="Heute + 12h"
                color={timeRange === 'today_plus_12h' ? 'primary' : 'default'}
                variant={timeRange === 'today_plus_12h' ? 'filled' : 'outlined'}
                onClick={() => { setTimeRange('today_plus_12h'); setPage(0); }}
              />
              <Chip
                clickable
                label="Offen"
                color={statusFilter === 'open' ? 'warning' : 'default'}
                variant={statusFilter === 'open' ? 'filled' : 'outlined'}
                onClick={() => { setStatusFilter(statusFilter === 'open' ? 'all' : 'open'); setPage(0); }}
              />
              <Chip
                clickable
                label="Geplant"
                color={statusFilter === 'scheduled' ? 'secondary' : 'default'}
                variant={statusFilter === 'scheduled' ? 'filled' : 'outlined'}
                onClick={() => { setStatusFilter(statusFilter === 'scheduled' ? 'all' : 'scheduled'); setPage(0); }}
              />
              <Chip
                clickable
                label="Vorbest."
                color={orderTypeFilter === '1' ? 'secondary' : 'default'}
                variant={orderTypeFilter === '1' ? 'filled' : 'outlined'}
                onClick={() => { setOrderTypeFilter(orderTypeFilter === '1' ? 'all' : '1'); setPage(0); }}
              />
              <Chip
                clickable
                label="Krankenfahrt"
                color={rideTypeFilter === 'krankenfahrt' ? 'error' : 'default'}
                variant={rideTypeFilter === 'krankenfahrt' ? 'filled' : 'outlined'}
                onClick={() => { setRideTypeFilter(rideTypeFilter === 'krankenfahrt' ? 'all' : 'krankenfahrt'); setPage(0); }}
              />
            </Stack>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 1 }}>
              <Typography variant="body2" color="text.secondary">
                {filteredData.length} Fahrten
              </Typography>
              <Button
                size="small"
                startIcon={<FilterListIcon />}
                onClick={() => setMobileFiltersOpen(open => !open)}
              >
                {mobileFiltersOpen ? 'Filter schließen' : 'Mehr Filter'}
              </Button>
            </Box>
          </Grid>
          <Grid size={{ xs: 12, sm: 2 }} sx={{ display: { xs: mobileFiltersOpen ? 'block' : 'none', sm: 'block' } }}>
            <FormControl fullWidth>
              <InputLabel id="order-type-filter-label">Bestellart</InputLabel>
              <Select
                labelId="order-type-filter-label"
                id="order-type-filter"
                value={orderTypeFilter}
                label="Bestellart"
                onChange={handleOrderTypeFilterChange}
              >
                <MenuItem value="all">Alle</MenuItem>
                <MenuItem value="1">Vorbestellungen</MenuItem>
                <MenuItem value="2">Sofortbestellungen</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, sm: 2 }} sx={{ display: { xs: mobileFiltersOpen ? 'block' : 'none', sm: 'block' } }}>
            <FormControl fullWidth>
              <InputLabel id="status-filter-label">Status</InputLabel>
              <Select
                labelId="status-filter-label"
                id="status-filter"
                value={statusFilter}
                label="Status"
                onChange={handleStatusFilterChange}
              >
                <MenuItem value="all">Alle Status</MenuItem>
                <MenuItem value="open">{RIDE_STATUS_FILTER_LABELS.open}</MenuItem>
                <MenuItem value="accepted">{RIDE_STATUS_FILTER_LABELS.accepted}</MenuItem>
                <MenuItem value="pre_accepted">{RIDE_STATUS_FILTER_LABELS.pre_accepted}</MenuItem>
                <MenuItem value="arrived">{RIDE_STATUS_FILTER_LABELS.arrived}</MenuItem>
                <MenuItem value="in_progress">{RIDE_STATUS_FILTER_LABELS.in_progress}</MenuItem>
                <MenuItem value="completed">{RIDE_STATUS_FILTER_LABELS.completed}</MenuItem>
                <MenuItem value="cancelled">{RIDE_STATUS_FILTER_LABELS.cancelled}</MenuItem>
                <MenuItem value="cancelled_by_driver">{RIDE_STATUS_FILTER_LABELS.cancelled_by_driver}</MenuItem>
                <MenuItem value="scheduled">{RIDE_STATUS_FILTER_LABELS.scheduled}</MenuItem>
                <MenuItem value="paused">{RIDE_STATUS_FILTER_LABELS.paused}</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, sm: 2 }} sx={{ display: { xs: mobileFiltersOpen ? 'block' : 'none', sm: 'block' } }}>
            <FormControl fullWidth>
              <InputLabel id="ride-type-filter-label">Fahrttyp</InputLabel>
              <Select
                labelId="ride-type-filter-label"
                id="ride-type-filter"
                value={rideTypeFilter}
                label="Fahrttyp"
                onChange={handleRideTypeFilterChange}
              >
                <MenuItem value="all">Alle</MenuItem>
                <MenuItem value="app">App-Fahrt</MenuItem>
                <MenuItem value="krankenfahrt">Krankenfahrt</MenuItem>
                <MenuItem value="rechnungsfahrt">Rechnungsfahrt</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, sm: 3 }} sx={{ display: { xs: mobileFiltersOpen ? 'block' : 'none', sm: 'block' } }}>
            <FormControl fullWidth>
              <InputLabel id="time-range-label">Zeitraum</InputLabel>
              <Select
                labelId="time-range-label"
                id="time-range"
                value={timeRange}
                label="Zeitraum"
                onChange={handleTimeRangeChange}
              >
                <MenuItem value="today_plus_12h">Heute + nächste 12h</MenuItem>
                <MenuItem value="next_24h">Nächste 24 Stunden</MenuItem>
                <MenuItem value="yesterday">Gestern</MenuItem>
                <MenuItem value="today">Heute</MenuItem>
                <MenuItem value="tomorrow">Morgen</MenuItem>
                <MenuItem value="this_week">Diese Woche</MenuItem>
                <MenuItem value="next_week">Nächste Woche</MenuItem>
                <MenuItem value="this_month">Dieser Monat</MenuItem>
                <MenuItem value="custom">Individueller Zeitraum…</MenuItem>
                <MenuItem value="all">Alle Zeiträume</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12 }} sx={{ display: { xs: 'none', sm: 'block' } }}>
            <Typography variant="body2" color="textSecondary" sx={{ textAlign: 'right' }}>
              {filteredData.length} Fahrten gefunden
            </Typography>
          </Grid>
          {timeRange === 'custom' && (
            <Grid size={{ xs: 12 }} sx={{ display: { xs: mobileFiltersOpen ? 'block' : 'none', sm: 'block' } }}>
              <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={de}>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  <DatePicker
                    label="Von"
                    value={customStart}
                    onChange={handleCustomStartChange}
                    slotProps={{ textField: { size: 'small' } }}
                  />
                  <DatePicker
                    label="Bis"
                    value={customEnd}
                    onChange={handleCustomEndChange}
                    slotProps={{ textField: { size: 'small' } }}
                  />
                </Box>
              </LocalizationProvider>
            </Grid>
          )}
        </Grid>
      </Paper>
      
      {/* Mobile Fahrtkarten */}
      <Box sx={{ display: { xs: 'flex', md: 'none' }, flexDirection: 'column', gap: 1.5 }}>
        {paginatedData.length > 0 ? (
          paginatedData.map((ride) => {
            const isOverdue = isRideOverdue(ride);
            const delayInfo = getDelayInfo(ride);
            const driverName = ride.driverName || getDriverDisplayName(ride.driver);
            const currentOfferDriver = ride.currentOfferDriverId
              ? driverById[ride.currentOfferDriverId]
              : null;
            const currentOfferDriverName =
              getDriverDisplayName(currentOfferDriver) ||
              ride.currentOfferDriverName ||
              (ride.currentOfferDriverId ? `Fahrer ${shortId(ride.currentOfferDriverId)}` : null);
            const currentOfferDriverNumber = currentOfferDriver?.driverNumber || null;
            const vModel = ride.driverModel || ride.vehicle?.model || vehicleByDriverId[ride.driverId]?.model;
            const vPlate = ride.driverLicensePlate || ride.vehicle?.licensePlate || vehicleByDriverId[ride.driverId]?.licensePlate;
            const vNumber = ride.vehicle?.vehicleNumber || vehicleByDriverId[ride.driverId]?.vehicleNumber;
            const displayStatus = getDisplayStatus(ride);
            const statusDetailLabel = getStatusDetailLabel(ride);
            const canAssign = ride.status === 'open' || ride.status === 'scheduled';

            return (
              <Card
                key={ride.id}
                variant="outlined"
                sx={{
                  borderLeft: isOverdue ? '4px solid #ff4444' : '4px solid transparent',
                  bgcolor: isOverdue ? 'rgba(255, 68, 68, 0.04)' : 'background.paper',
                }}
              >
                <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, alignItems: 'flex-start' }}>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="caption" color="text.secondary">
                        Erstellt: {formatDate(ride.timestamp || ride.request_time)}
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap', mt: 0.5 }}>
                        <StatusChip
                          label={translateStatus(ride, displayStatus)}
                          status={displayStatus}
                          size="small"
                          icon={getIconForStatus(displayStatus)}
                          sx={isOverdue ? { backgroundColor: '#ff4444 !important' } : {}}
                        />
                        {statusDetailLabel && (
                          <Typography
                            variant="caption"
                            color="error.main"
                            fontWeight="bold"
                            display="block"
                            sx={{ width: '100%' }}
                          >
                            {statusDetailLabel}
                          </Typography>
                        )}
                        <Chip
                          icon={(ride.orderType === 1 || ride.convertedToOpenRide) ? <EventIcon fontSize="small" /> : <FlashOnIcon fontSize="small" />}
                          label={(ride.orderType === 1 || ride.convertedToOpenRide) ? 'Vorbestellung' : 'Sofort'}
                          size="small"
                          color={(ride.orderType === 1 || ride.convertedToOpenRide) ? 'secondary' : 'primary'}
                          variant="outlined"
                        />
                        {ride.isKrankenfahrt && (
                          <Chip size="small" label="Krankenfahrt" color="error" variant="outlined" />
                        )}
                        <PaymentChip ride={ride} sx={{ height: 20, fontSize: '0.7rem' }} />
                        {(ride.source === 'ai_telephonist' || ride.createdByRole === 'ai_telephonist') && (
                          <Chip
                            label="🤖 KI-Buchung"
                            size="small"
                            variant="outlined"
                            sx={{
                              height: 20,
                              fontSize: '0.7rem',
                              borderColor: 'info.main',
                              color: 'info.dark',
                              '& .MuiChip-icon': { color: 'info.dark', fontSize: '0.8rem' },
                            }}
                          />
                        )}
                        {isVenueBooking(ride) && (
                          <Chip
                            label={ride.venueName ? `🛎️ ${ride.venueName}` : '🛎️ Venue-Bestellung'}
                            size="small"
                            variant="outlined"
                            sx={{
                              height: 20,
                              fontSize: '0.7rem',
                              borderColor: 'warning.main',
                              color: 'warning.dark',
                              '& .MuiChip-icon': { color: 'warning.dark', fontSize: '0.8rem' },
                            }}
                          />
                        )}
                        {renderMedicalStatusIcon(ride)}
                      </Box>
                    </Box>
                    <IconButton
                      aria-label="Aktionen"
                      aria-controls={`ride-menu-${ride.id}`}
                      aria-haspopup="true"
                      onClick={(e) => handleOpenMenu(e, ride)}
                    >
                      <MoreVertIcon />
                    </IconButton>
                  </Box>

                  {isOverdue && (
                    <Typography variant="caption" sx={{ color: '#ff4444', fontWeight: 'bold', display: 'block', mt: 1 }}>
                      Überfällig oder unmittelbar fällig
                    </Typography>
                  )}

                  <Box sx={{ mt: 1.25, display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, overflowWrap: 'anywhere' }}>
                      Von: {ride.start || ride.pickup_address || '-'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ overflowWrap: 'anywhere' }}>
                      Nach: {ride.destination || ride.destination_address || 'Kein Ziel angegeben'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Kunde: {ride.customerName || ride.customer?.name || 'Unbekannt'}
                    </Typography>
                    {ride.notes && (
                      <Box
                        title={ride.notes}
                        sx={{
                          display: 'inline-flex',
                          alignItems: 'flex-start',
                          gap: 0.5,
                          mt: 0.25,
                          px: 0.75,
                          py: 0.25,
                          maxWidth: '100%',
                          borderRadius: 0.75,
                          bgcolor: 'rgba(255, 213, 79, 0.35)',
                          borderLeft: '3px solid',
                          borderColor: 'warning.main',
                        }}
                      >
                        <NoteIcon sx={{ fontSize: '0.95rem', color: 'warning.dark', flexShrink: 0, mt: '1px' }} />
                        <Typography
                          variant="caption"
                          sx={{
                            fontStyle: 'italic',
                            fontWeight: 600,
                            color: 'warning.dark',
                            overflowWrap: 'anywhere',
                            minWidth: 0,
                          }}
                        >
                          Notiz: {ride.notes}
                        </Typography>
                      </Box>
                    )}
                  </Box>

                  <Divider sx={{ my: 1.25 }} />

                  <Grid container spacing={1}>
                    <Grid size={6}>
                      <Typography variant="caption" color="text.secondary">Zeit</Typography>
                      <Typography variant="body2">
                        {(() => {
                          // Abgeschlossene Fahrten: tatsächlich gefahrene Zeit
                          // statt der Schätzung, falls aus den Zeitstempeln
                          // ableitbar.
                          const actualMinutes = getActualRideDurationMinutes(ride);
                          if (actualMinutes !== null) {
                            return `${actualMinutes} Min. gefahren`;
                          }
                          if (ride.orderType === 1 && ride.scheduledTime) {
                            return formatDate(ride.scheduledTime);
                          }
                          return ride.estimatedTimeMinutes
                            ? `${ride.estimatedTimeMinutes} Min. geschätzt`
                            : 'Sofort';
                        })()}
                      </Typography>
                      {(ride.status === 'scheduled' || ride.status === 'pre_accepted') && ride.dispatchOpenAt && (
                        <Typography variant="caption" sx={{ display: 'block', color: 'warning.dark', fontWeight: 600, mt: 0.5 }}>
                          {ride.status === 'pre_accepted' ? 'Online-Prüfung' : 'Freigabe'}: {formatTime(ride.dispatchOpenAt)}
                          {Number.isFinite(ride.preorderDispatchLeadMinutes)
                            ? ` (${ride.preorderDispatchLeadMinutes} Min. Vorlauf)`
                            : ''}
                        </Typography>
                      )}
                    </Grid>
                    <Grid size={6}>
                      <Typography variant="caption" color="text.secondary">Fahrer</Typography>
                      {driverName ? (
                        <Typography variant="body2">{driverName}</Typography>
                      ) : ride.currentOfferDriverId ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                          <Chip label="Angefragt" size="small" color="warning" sx={{ height: 18, fontSize: '0.64rem' }} />
                          {currentOfferDriverNumber && (
                            <Chip label={currentOfferDriverNumber} size="small" color="primary" variant="filled" sx={{ height: 18, fontSize: '0.64rem', fontWeight: 700 }} />
                          )}
                          <Typography variant="body2" sx={{ overflowWrap: 'anywhere' }}>
                            {currentOfferDriverName || 'Unbekannt'}
                          </Typography>
                        </Box>
                      ) : (
                        <Typography variant="body2">Nicht zugewiesen</Typography>
                      )}
                    </Grid>
                    <Grid size={6}>
                      <Typography variant="caption" color="text.secondary">Fahrzeugtyp</Typography>
                      <Typography variant="body2">
                        {formatVehicleWithCapabilities(ride.vehicleType, ride.specialCapabilities || migrateSpecialVehicleType(ride))}
                      </Typography>
                    </Grid>
                    <Grid size={6}>
                      <Typography variant="caption" color="text.secondary">Fahrzeug</Typography>
                      {vModel || vPlate ? (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                          {vNumber && (
                            <Chip
                              label={vNumber}
                              size="small"
                              color="primary"
                              variant="filled"
                              sx={{ height: 20, fontWeight: 700, fontSize: '0.7rem', alignSelf: 'flex-start' }}
                            />
                          )}
                          <Typography variant="body2">
                            {vModel || ''}{vPlate ? ` (${vPlate})` : ''}
                          </Typography>
                        </Box>
                      ) : (
                        <Typography variant="body2">Nicht zugewiesen</Typography>
                      )}
                    </Grid>
                  </Grid>

                  {delayInfo && (
                    <Typography variant="caption" sx={{ display: 'block', mt: 1, color: delayInfo.status === 'pending' ? '#ff4444' : 'text.secondary' }}>
                      {delayInfo.status === 'pending'
                        ? `${delayInfo.delayMinutes} Min. verspätet`
                        : delayInfo.status === 'delayed'
                          ? `${delayInfo.delayMinutes} Min. verspätet gestartet`
                          : delayInfo.status === 'early'
                            ? `${delayInfo.delayMinutes} Min. früher gestartet`
                            : 'Pünktlich gestartet'}
                    </Typography>
                  )}

                  <Box sx={{ display: 'flex', gap: 1, mt: 1.5 }}>
                    <Button
                      fullWidth
                      variant="outlined"
                      size="small"
                      component={Link}
                      to={`/fahrten/${ride.id}`}
                    >
                      Anzeigen
                    </Button>
                    {canAssign && (
                      <Button
                        fullWidth
                        variant="contained"
                        size="small"
                        onClick={() => openRideDialog?.({ mode: 'assign', rideId: ride.id })}
                        startIcon={<TaxiIcon />}
                      >
                        Zuweisen
                      </Button>
                    )}
                  </Box>
                </CardContent>
              </Card>
            );
          })
        ) : (
          <Paper sx={{ p: 3, textAlign: 'center' }}>
            <Typography variant="body1">Keine Fahrten gefunden</Typography>
          </Paper>
        )}
        <TablePagination
          rowsPerPageOptions={isMobile ? [10, 25] : [10, 25, 50]}
          component="div"
          count={filteredData.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="Pro Seite:"
          labelDisplayedRows={({ from, to, count }) => `${from}–${to} von ${count}`}
          sx={{
            bgcolor: 'background.paper',
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
            '.MuiTablePagination-toolbar': {
              px: 1,
              flexWrap: 'wrap',
              justifyContent: 'center',
            },
          }}
        />
      </Box>

      {/* Desktop Fahrtentabelle */}
      <TableContainer component={Paper} sx={{ display: { xs: 'none', md: 'block' } }}>
        <Table sx={{ minWidth: 900 }} aria-label="Fahrtentabelle">
          <TableHead>
            <TableRow>
              <TableCell sx={{ maxWidth: 360, minWidth: 250 }}>Fahrt</TableCell>
              <TableCell align="center">Status</TableCell>
              <TableCell align="center">Fahrpreis</TableCell>
              <TableCell align="center">Verordnung</TableCell>
              <TableCell align="center">Fahrzeugtyp</TableCell>
              <TableCell align="center" sx={{ width: '1%', whiteSpace: 'nowrap' }}>Zeiten</TableCell>
              <TableCell align="center">Fahrer</TableCell>
              <TableCell align="center">Fahrzeug</TableCell>
              <TableCell align="right">Aktionen</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedData.length > 0 ? (
              paginatedData.map((ride) => {
                const isOverdue = isRideOverdue(ride);
                const delayInfo = getDelayInfo(ride);
                return (
                  <StyledTableRow key={ride.id} isOverdue={isOverdue}>
                    <TableCell component="th" scope="row" sx={{ maxWidth: 360, minWidth: 250 }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                      <Typography variant="caption" color="text.secondary">
                        Erstellt: {formatDate(ride.timestamp || ride.request_time)}
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                          <StyledBadge
                            badgeContent={ride.convertedToOpenRide ? "!" : 0}
                            color="error"
                            anchorOrigin={{
                              vertical: 'top',
                              horizontal: 'right',
                            }}
                          >
                            <Chip
                              icon={(ride.orderType === 1 || ride.convertedToOpenRide) ? <EventIcon fontSize="small" /> : <FlashOnIcon fontSize="small" />}
                              label={(ride.orderType === 1 || ride.convertedToOpenRide) ? "Vorbestellung" : "Sofortbestellung"}
                              size="small"
                              color={(ride.orderType === 1 || ride.convertedToOpenRide) ? "secondary" : "primary"}
                              variant="outlined"
                            />
                          </StyledBadge>
                          {(ride.isRecurring || ride.recurringOrderId) && (
                            <RecurringIcon
                              fontSize="small"
                              sx={{ color: 'primary.main' }}
                            />
                          )}
	                          {ride.isAnschlusstour && (
	                            <Chip
                              label="AT"
                              size="small"
                              color="success"
                              variant="outlined"
                              title="Anschlusstour"
	                              sx={{ height: 20, fontSize: '0.7rem' }}
	                            />
	                          )}
	                          {isAutoAcceptedOpenPreOrder(ride) && (
	                            <Chip
	                              label="Automatisch bestätigt"
	                              size="small"
	                              color="success"
	                              variant="outlined"
	                              sx={{ height: 20, fontSize: '0.7rem' }}
	                            />
	                          )}
	                          <Chip
                            icon={ride.isPhoneCustomer ? <PhoneIcon fontSize="small" /> : <SmartphoneIcon fontSize="small" />}
                            label={ride.isPhoneCustomer ? "Telefon" : "App"}
                            size="small"
                            variant="outlined"
                            sx={{
                              height: 20,
                              fontSize: '0.7rem',
                              borderColor: ride.isPhoneCustomer ? 'warning.main' : 'success.main',
                              color: ride.isPhoneCustomer ? 'warning.dark' : 'success.dark',
                              '& .MuiChip-icon': {
                                color: ride.isPhoneCustomer ? 'warning.dark' : 'success.dark',
                                fontSize: '0.8rem',
                              },
                            }}
                          />
                          {(ride.source === 'ai_telephonist' || ride.createdByRole === 'ai_telephonist') && (
                            <Chip
                                label="🤖 KI-Buchung"
                              size="small"
                              variant="outlined"
                              sx={{
                                height: 20,
                                fontSize: '0.7rem',
                                borderColor: 'info.main',
                                color: 'info.dark',
                                '& .MuiChip-icon': { color: 'info.dark', fontSize: '0.8rem' },
                              }}
                            />
                          )}
                          {isVenueBooking(ride) && (
                            <Chip
                              label={ride.venueName ? `🛎️ ${ride.venueName}` : '🛎️ Venue-Bestellung'}
                              size="small"
                              variant="outlined"
                              sx={{
                                height: 20,
                                fontSize: '0.7rem',
                                borderColor: 'warning.main',
                                color: 'warning.dark',
                                '& .MuiChip-icon': { color: 'warning.dark', fontSize: '0.8rem' },
                              }}
                            />
                          )}
                          <PaymentChip ride={ride} sx={{ height: 20, fontSize: '0.7rem' }} />
                        </Box>
                      <Typography variant="body2" color="textSecondary" noWrap>
                        Von: {ride.start || ride.pickup_address}
                      </Typography>
                      <Typography variant="body2" color="textSecondary" noWrap>
                        Nach: {ride.destination || ride.destination_address}
                      </Typography>
                      <Typography variant="body2" color="textSecondary" noWrap>
                        Kunde: {ride.customerName || ride.customer?.name || 'Unbekannt'}
                      </Typography>
                      {ride.notes && (
                        <Box
                          title={ride.notes}
                          sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 0.5,
                            mt: 0.25,
                            px: 0.75,
                            py: 0.125,
                            maxWidth: 250,
                            borderRadius: 0.75,
                            bgcolor: 'rgba(255, 213, 79, 0.35)',
                            borderLeft: '3px solid',
                            borderColor: 'warning.main',
                          }}
                        >
                          <NoteIcon sx={{ fontSize: '0.85rem', color: 'warning.dark', flexShrink: 0 }} />
                          <Typography
                            variant="caption"
                            noWrap
                            sx={{
                              fontStyle: 'italic',
                              fontWeight: 600,
                              color: 'warning.dark',
                              minWidth: 0,
                            }}
                          >
                            Notiz: {ride.notes}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell align="center">
                    <Box sx={{ position: 'relative' }}>
                      {(() => {
                        const displayStatus = getDisplayStatus(ride);
                        const statusDetailLabel = getStatusDetailLabel(ride);
                        return (
                          <>
                            <StatusChip
                              label={translateStatus(ride, displayStatus)}
                              status={displayStatus}
                              size="small"
                              icon={getIconForStatus(displayStatus)}
                              sx={isOverdue ? {
                                backgroundColor: '#ff4444 !important',
                                color: 'white !important',
                                animation: 'pulse 2s infinite',
                                '@keyframes pulse': {
                                  '0%': { transform: 'scale(1)' },
                                  '50%': { transform: 'scale(1.05)' },
                                  '100%': { transform: 'scale(1)' },
                                },
                              } : {}}
                            />
                            {statusDetailLabel && (
                              <Typography
                                variant="caption"
                                display="block"
                                color="error.main"
                                fontWeight="bold"
                                sx={{ mt: 0.5 }}
                              >
                                {statusDetailLabel}
                              </Typography>
                            )}
                          </>
                        );
                      })()}
                      {isOverdue && (
                        <Typography variant="caption" display="block" sx={{ 
                          mt: 0.5, 
                          color: '#ff4444', 
                          fontWeight: 'bold',
                          fontSize: '0.7rem'
                        }}>
                          ⚠️ ÜBERFÄLLIG
                        </Typography>
                      )}
                    </Box>
                    {(ride.scheduledTime || ride.orderType === 1) && (
                      <Typography variant="caption" display="block" sx={{ 
                        mt: 1,
                        color: isOverdue ? '#ff4444' : 'text.secondary',
                        fontWeight: isOverdue ? 'bold' : 'normal'
                      }}>
                        Geplant für: {formatDate(ride.scheduledTime || ride.scheduled_time)}
                      </Typography>
                    )}
                    {delayInfo && (
                      <Box sx={{ mt: 0.5, display: 'flex', justifyContent: 'center' }}>
                        {delayInfo.status === 'pending' && (
                          <Typography variant="caption" sx={{ 
                            color: '#ff4444', 
                            fontWeight: 'bold',
                            fontSize: '0.7rem',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 0.5
                          }}>
                            ⏰ {delayInfo.delayMinutes} Min. verspätet
                          </Typography>
                        )}
                        {delayInfo.status === 'delayed' && (
                          <Typography variant="caption" sx={{ 
                            color: '#ff6b00', 
                            fontWeight: 'bold',
                            fontSize: '0.7rem',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 0.5
                          }}>
                            🕐 {delayInfo.delayMinutes} Min. verspätet gestartet
                          </Typography>
                        )}
                        {delayInfo.status === 'early' && (
                          <Typography variant="caption" sx={{ 
                            color: '#4caf50', 
                            fontWeight: 'bold',
                            fontSize: '0.7rem',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 0.5
                          }}>
                            ✅ {delayInfo.delayMinutes} Min. früher gestartet
                          </Typography>
                        )}
                        {delayInfo.status === 'ontime' && (
                          <Typography variant="caption" sx={{ 
                            color: '#4caf50', 
                            fontWeight: 'bold',
                            fontSize: '0.7rem',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 0.5
                          }}>
                            ✅ Pünktlich gestartet
                          </Typography>
                        )}
                      </Box>
                    )}
                  </TableCell>
                  <TableCell align="center">
                    {renderFareCell(ride)}
                  </TableCell>
                  <TableCell align="center">
                    {renderMedicalTransportInfo(ride)}
                  </TableCell>
                  <TableCell align="center">
                    {formatVehicleWithCapabilities(ride.vehicleType, ride.specialCapabilities || migrateSpecialVehicleType(ride))}
                  </TableCell>
                  <TableCell align="center">
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                      
                      {/* Geplante Zeit für Vorbestellungen */}
                      {ride.orderType === 1 && ride.scheduledTime &&
                        renderTimeRow('Geplant:', formatDate(ride.scheduledTime), 'scheduled')}

                      {/* Akzeptiert am – bei zugewiesenen Vorbestellungen direkt unter „Geplant" */}
                      {(ride.acceptedTimestamp || ride.acceptedAt) &&
                        renderTimeRow('Akzeptiert:', formatDate(ride.acceptedTimestamp || ride.acceptedAt), 'accepted')}

                      {/* Berechnete Freigabezeit (Disposition) für geplante/zugewiesene Vorbestellungen */}
                      {(ride.status === 'scheduled' || ride.status === 'pre_accepted') && ride.dispatchOpenAt && (
                        <>
                          {renderTimeRow(
                            ride.status === 'pre_accepted' ? 'Online-Prüfung:' : 'Freigabe:',
                            formatDate(ride.dispatchOpenAt),
                            ride.status === 'pre_accepted' ? 'pre_accepted' : 'scheduled',
                            'warning.main'
                          )}
                          {ride.pickupServiceAreaStatus && (
                            <Typography variant="caption" color="textSecondary" noWrap>
                              {{
                                inside: 'Im Betriebsgebiet',
                                outside: 'Außerhalb Betriebsgebiet',
                                unknown: 'Betriebsgebiet unbekannt',
                              }[ride.pickupServiceAreaStatus] || ride.pickupServiceAreaStatus}
                            </Typography>
                          )}
                          {(Number.isFinite(ride.preorderDispatchLeadMinutes) || Number.isFinite(ride.preorderApproachEstimateMinutes)) && (
                            <Typography variant="caption" color="textSecondary" noWrap>
                              {[
                                Number.isFinite(ride.preorderDispatchLeadMinutes)
                                  ? `(${ride.preorderDispatchLeadMinutes} Min. Vorlauf)`
                                  : null,
                                Number.isFinite(ride.preorderApproachEstimateMinutes)
                                  ? `Anfahrt ~${ride.preorderApproachEstimateMinutes} Min.`
                                  : null,
                              ].filter(Boolean).join(' · ')}
                            </Typography>
                          )}
                        </>
                      )}

                      {/* Ankunft */}
                      {ride.arrivedTimestamp &&
                        renderTimeRow('Ankunft:', formatDate(ride.arrivedTimestamp), 'arrived')}
                      
                      {/* Fahrgast abgeholt */}
                      {ride.passengerPickedUpTimestamp &&
                        renderTimeRow('Abgeholt:', formatDate(ride.passengerPickedUpTimestamp), 'in_progress')}
                      
                      {/* Abschluss */}
                      {(ride.completedTimestamp || ride.completionTimestamp) &&
                        renderTimeRow('Abschluss:', formatDate(ride.completedTimestamp || ride.completionTimestamp), 'completed')}

                      {/* Storniert */}
                      {(ride.status === 'cancelled' || ride.status === 'cancelled_by_driver') &&
                        renderTimeRow('Storniert:', formatDate(ride.cancelledTimestamp || ride.cancelledAt || ride.timestamp), ride.status)}
                      
                      {/* Fahrzeit: tatsächlich gefahren (abgeschlossen) sonst Schätzung */}
                      {(() => {
                        const actualMinutes = getActualRideDurationMinutes(ride);
                        if (actualMinutes !== null) {
                          return (
                            <Typography variant="caption" color="textSecondary">
                              <strong>Fahrzeit:</strong> {actualMinutes} Min.
                            </Typography>
                          );
                        }
                        return ride.estimatedTimeMinutes ? (
                          <Typography variant="caption" color="textSecondary">
                            <strong>Schätzung:</strong> {ride.estimatedTimeMinutes} Min.
                          </Typography>
                        ) : null;
                      })()}
                    </Box>
                  </TableCell>
                  <TableCell align="center">
                    {(() => {
                      const assignedDriverName = ride.driverName || getDriverDisplayName(ride.driver);
                      const assignedDriverNumber =
                        ride.driver?.driverNumber ||
                        (ride.driverId ? driverById[ride.driverId]?.driverNumber : null);
                      const currentOfferDriver = ride.currentOfferDriverId
                        ? driverById[ride.currentOfferDriverId]
                        : null;
                      const currentOfferDriverName =
                        getDriverDisplayName(currentOfferDriver) ||
                        ride.currentOfferDriverName ||
                        (ride.currentOfferDriverId ? `Fahrer ${shortId(ride.currentOfferDriverId)}` : null);
                      const currentOfferDriverNumber = currentOfferDriver?.driverNumber || null;
                      const offerLabel =
                        OFFER_TYPE_LABEL_MAP[ride.currentOfferType] ||
                        DISPATCH_SOURCE_LABEL_MAP[ride.dispatchOfferSource] ||
                        'Angebot läuft';

                      if (ride.driver || ride.driverId) {
                        return (
                          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <Badge
                                color={ride.driver?.status === 'available' ? 'success' :
                                       ride.driver?.status === 'busy' ? 'primary' : 'default'}
                                variant="dot"
                                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                              >
                                <DriverIcon />
                              </Badge>
                              {assignedDriverNumber && (
                                <Chip
                                  label={assignedDriverNumber}
                                  size="small"
                                  color="primary"
                                  variant="filled"
                                  sx={{ height: 20, fontWeight: 700, fontSize: '0.7rem' }}
                                />
                              )}
                            </Box>
                            <Typography variant="body2" sx={{ mt: 0.5 }}>
                              {assignedDriverName || 'Unbekannt'}
                            </Typography>
                          </Box>
                        );
                      }

                      if (ride.currentOfferDriverId) {
                        return (
                          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <Badge
                                color="warning"
                                variant="dot"
                                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                              >
                                <DriverIcon color="warning" />
                              </Badge>
                              {currentOfferDriverNumber && (
                                <Chip
                                  label={currentOfferDriverNumber}
                                  size="small"
                                  color="warning"
                                  variant="outlined"
                                  sx={{ height: 20, fontWeight: 700, fontSize: '0.7rem' }}
                                />
                              )}
                            </Box>
                            <Typography variant="caption" color="warning.main" fontWeight="bold">
                              Angefragt
                            </Typography>
                            <Typography variant="body2">
                              {currentOfferDriverName || 'Unbekannt'}
                            </Typography>
                            <Chip
                              size="small"
                              color={ride.currentOfferType === 'auto_follow_up' ? 'info' : 'warning'}
                              variant="outlined"
                              label={offerLabel}
                            />
                            {ride.currentOfferExpiresAt && (
                              <Typography variant="caption" color="textSecondary">
                                bis {formatDate(ride.currentOfferExpiresAt)}
                              </Typography>
                            )}
                          </Box>
                        );
                      }

                      return (
                        <Typography variant="body2" color="textSecondary">
                          Nicht zugewiesen
                        </Typography>
                      );
                    })()}
                  </TableCell>
                  <TableCell align="center">
                    {(() => {
                      const vModel = ride.driverModel || ride.vehicle?.model || vehicleByDriverId[ride.driverId]?.model;
                      const vPlate = ride.driverLicensePlate || ride.vehicle?.licensePlate || vehicleByDriverId[ride.driverId]?.licensePlate;
                      const vNumber = ride.vehicle?.vehicleNumber || vehicleByDriverId[ride.driverId]?.vehicleNumber;
                      return vModel || vPlate ? (
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <CarIcon />
                            {vNumber && (
                              <Chip
                                label={vNumber}
                                size="small"
                                color="primary"
                                variant="filled"
                                sx={{ height: 20, fontWeight: 700, fontSize: '0.7rem' }}
                              />
                            )}
                          </Box>
                          <Typography variant="body2" sx={{ mt: 0.5 }}>
                            {vModel || ''}
                            {vPlate && ` (${vPlate})`}
                          </Typography>
                        </Box>
                      ) : (
                        <Typography variant="body2" color="textSecondary">
                          Nicht zugewiesen
                        </Typography>
                      );
                    })()}
                  </TableCell>
                  <TableCell align="right">
                    <IconButton
                      aria-label="Aktionen"
                      aria-controls={`ride-menu-${ride.id}`}
                      aria-haspopup="true"
                      onClick={(e) => handleOpenMenu(e, ride)}
                    >
                      <MoreVertIcon />
                    </IconButton>
                    </TableCell>
                  </StyledTableRow>
                );
              })
            ) : (
              <TableRow>
                <TableCell colSpan={9} align="center">
                  <Typography variant="body1" sx={{ py: 3 }}>
                    Keine Fahrten gefunden
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[10, 25, 50]}
          component="div"
          count={filteredData.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="Zeilen pro Seite:"
          labelDisplayedRows={
            ({ from, to, count }) => `${from}–${to} von ${count}`
          }
        />
      </TableContainer>
      
      {/* Aktions-Menü */}
      <Menu
        id={menuRide ? `ride-menu-${menuRide.id}` : undefined}
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleCloseMenu}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <MenuItem 
          component={Link} 
          to={menuRide ? `/fahrten/${menuRide.id}` : '#'} 
          onClick={handleCloseMenu}
        >
          Fahrt anzeigen
        </MenuItem>
        {menuRide && canEditRide(menuRide) && !FINAL_RIDE_STATUSES.includes(menuRide.status) && menuRide.status !== 'open' && menuRide.status !== 'scheduled' && (
          <MenuItem
            onClick={() => { const rideId = menuRide?.id; handleCloseMenu(); openRideDialog?.({ mode: 'edit', rideId }); }}
          >
            <EditIcon fontSize="small" sx={{ mr: 1 }} />
            Bearbeiten
          </MenuItem>
        )}
        {menuRide && ASSIGNABLE_RIDE_STATUSES.includes(menuRide.status) && (
          <MenuItem
            onClick={() => { const rideId = menuRide?.id; handleCloseMenu(); openRideDialog?.({ mode: 'assign', rideId }); }}
          >
            <TaxiIcon fontSize="small" sx={{ mr: 1 }} />
            {menuRide.driverId ? 'Fahrer neu zuweisen' : 'Fahrer zuweisen'}
          </MenuItem>
        )}
        {menuRide && ['accepted', 'arrived'].includes(menuRide.status) && (
          <MenuItem onClick={() => handleRideStatusAction(menuRide, 'in_progress')}>
            <StartIcon fontSize="small" sx={{ mr: 1 }} />
            Fahrgast zugestiegen
          </MenuItem>
        )}
        {menuRide && ['arrived', 'in_progress'].includes(menuRide.status) && (
          <MenuItem onClick={() => handleRideStatusAction(menuRide, 'completed')}>
            <EndIcon fontSize="small" sx={{ mr: 1 }} />
            Fahrt abschließen
          </MenuItem>
        )}
        {menuRide && menuRide.status === 'pre_accepted' && (
          <MenuItem onClick={() => handleReleaseRide(menuRide)}>
            <SwapHorizIcon fontSize="small" sx={{ mr: 1 }} />
            Fahrt zurücknehmen
          </MenuItem>
        )}
        {menuRide && (menuRide.status === 'scheduled' || menuRide.status === 'pre_accepted') && !menuRide.isPhoneCustomer && (!menuRide.gegenvorschlagStatus || menuRide.gegenvorschlagStatus === 'expired') && (
          <MenuItem
            component={Link}
            to={menuRide ? `/fahrten/${menuRide.id}` : '#'}
            onClick={handleCloseMenu}
          >
            <SwapHorizIcon fontSize="small" sx={{ mr: 1 }} />
            Gegenvorschlag senden
          </MenuItem>
        )}
        {menuRide && DUPLICATABLE_RIDE_STATUSES.includes(menuRide.status) && (
          <MenuItem
            onClick={() => {
              const duplicateRide = menuRide;
              handleCloseMenu();
              openRideDialog?.({ mode: 'create', draft: { duplicateRide } });
            }}
          >
            <ContentCopyIcon fontSize="small" sx={{ mr: 1 }} />
            {['completed', 'cancelled', 'cancelled_by_driver'].includes(menuRide.status)
              ? 'Fahrt erneut erstellen'
              : '2. Taxi anfordern'}
          </MenuItem>
        )}
        {menuRide && CANCELLABLE_RIDE_STATUSES.includes(menuRide.status) && (
          <MenuItem
            onClick={() => handleCancelRide(menuRide)}
            sx={{ color: 'error.main' }}
          >
            <CancelledIcon fontSize="small" sx={{ mr: 1 }} />
            Stornieren
          </MenuItem>
        )}
        {canDeleteRides && menuRide ? (
          <MenuItem
            onClick={() => handleDeleteClick(menuRide)}
            sx={{ color: 'error.main' }}
          >
            <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
            Löschen
          </MenuItem>
        ) : null}
      </Menu>

      {/* Lösch-Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle id="delete-dialog-title">
          Fahrt löschen
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-dialog-description">
            Sind Sie sicher, dass Sie diese Fahrt endgültig löschen möchten? 
            Diese Aktion kann nicht rückgängig gemacht werden.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} color="primary">
            Abbrechen
          </Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            Löschen
          </Button>
        </DialogActions>
      </Dialog>

      {/* Hinweis für Fahrten, die ohne Zieladresse erstellt wurden */}
      <NoDestinationRideSnackbar />
    </Box>
  );
};

export default RidesList;
